import { useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  DceItemHumanStatus,
  DceRequirementType,
  ListRequirementsFilters,
} from '../../types/dce-analysis.types';
import {
  useBulkValidateRequirements,
  useDceRequirements,
} from '../../hooks/useDceAnalysis';
import { RequirementCard } from './RequirementCard';

interface Props {
  opportunityId: number;
}

const TYPE_OPTIONS: DceRequirementType[] = [
  'technique',
  'fonctionnelle',
  'administrative',
  'qualite',
  'securite',
  'rgpd',
  'methodologique',
];

const STATUS_OPTIONS: DceItemHumanStatus[] = [
  'EXTRACTED',
  'VALIDATED',
  'REJECTED',
  'CORRECTED',
];

export function RequirementsSection({ opportunityId }: Props) {
  const [filters, setFilters] = useState<ListRequirementsFilters>({});
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const queryFilters = useMemo<ListRequirementsFilters>(
    () => ({ ...filters, search: search.trim() || undefined }),
    [filters, search],
  );

  const { data: requirements, isLoading } = useDceRequirements(opportunityId, queryFilters);
  const bulkMutation = useBulkValidateRequirements(opportunityId);

  // Sprint 4.E — Virtualization au-delà de 30 items (cf. plan §5.5).
  // Estimation 280 px par card (statement + meta + source + notes).
  const parentRef = useRef<HTMLDivElement>(null);
  const VIRTUALIZE_THRESHOLD = 30;
  const shouldVirtualize = (requirements?.length ?? 0) > VIRTUALIZE_THRESHOLD;
  const rowVirtualizer = useVirtualizer({
    count: requirements?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 280,
    overscan: 4,
    enabled: shouldVirtualize,
  });

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (!requirements) return;
    setSelectedIds(new Set(requirements.map((r) => r.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectHighConfidence = () => {
    if (!requirements) return;
    setSelectedIds(new Set(requirements.filter((r) => r.confidence >= 0.9).map((r) => r.id)));
  };

  const handleBulk = (status: 'VALIDATED' | 'REJECTED') => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkMutation.mutate(
      { ids, status },
      {
        onSuccess: (res) => {
          const verb = status === 'VALIDATED' ? 'validées' : 'rejetées';
          toast.success(
            `${res.updated.length} exigence${res.updated.length > 1 ? 's' : ''} ${verb}` +
              (res.alreadyInStatus.length ? ` (${res.alreadyInStatus.length} déjà dans ce statut)` : '') +
              (res.notFound.length ? ` — ${res.notFound.length} introuvable(s)` : ''),
          );
          clearSelection();
        },
        onError: () => toast.error('Erreur lors du bulk-validate'),
      },
    );
  };

  const total = requirements?.length ?? 0;
  const selected = selectedIds.size;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Exigences techniques et fonctionnelles {total > 0 && `(${total})`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filtres */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs h-8"
          />
          <Select
            value={filters.type ?? '_ALL_'}
            onValueChange={(v) =>
              setFilters((p) => ({
                ...p,
                type: v === '_ALL_' ? undefined : (v as DceRequirementType),
              }))
            }
          >
            <SelectTrigger className="w-44 h-8">
              <SelectValue placeholder="Tous les types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_ALL_">Tous les types</SelectItem>
              {TYPE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t} className="capitalize">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.humanStatus ?? '_ALL_'}
            onValueChange={(v) =>
              setFilters((p) => ({
                ...p,
                humanStatus: v === '_ALL_' ? undefined : (v as DceItemHumanStatus),
              }))
            }
          >
            <SelectTrigger className="w-44 h-8">
              <SelectValue placeholder="Tous statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_ALL_">Tous statuts</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s.toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setFilters((p) => ({
                ...p,
                humanStatus: p.humanStatus === 'EXTRACTED' ? undefined : 'EXTRACTED',
              }))
            }
            className={`h-8 text-xs ${filters.humanStatus === 'EXTRACTED' ? 'bg-muted' : ''}`}
          >
            À valider seulement
          </Button>
        </div>

        {/* Bulk actions bar */}
        {selected > 0 && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2 text-sm">
            <span className="font-medium">{selected} sélectionnée{selected > 1 ? 's' : ''}</span>
            <span className="text-muted-foreground mx-1">·</span>
            <Button
              size="sm"
              onClick={() => handleBulk('VALIDATED')}
              disabled={bulkMutation.isPending}
              className="h-7"
            >
              {bulkMutation.isPending && bulkMutation.variables?.status === 'VALIDATED' && (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              )}
              <Check className="h-3.5 w-3.5 mr-1" />
              Valider la sélection
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleBulk('REJECTED')}
              disabled={bulkMutation.isPending}
              className="h-7"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Rejeter la sélection
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="h-7 text-xs"
            >
              Désélectionner
            </Button>
          </div>
        )}

        {selected === 0 && total > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <button onClick={selectAll} className="underline hover:text-foreground">
              Tout sélectionner
            </button>
            <span>·</span>
            <button onClick={selectHighConfidence} className="underline hover:text-foreground">
              Sélectionner confiance ≥ 90%
            </button>
          </div>
        )}

        {/* Liste */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-copper" />
          </div>
        )}
        {!isLoading && total === 0 && (
          <p className="text-sm text-muted-foreground italic py-4">
            Aucune exigence à afficher avec ces filtres.
          </p>
        )}
        {!isLoading && requirements && !shouldVirtualize && (
          <div className="space-y-2 max-h-[700px] overflow-y-auto pr-1">
            {requirements.map((r) => (
              <RequirementCard
                key={r.id}
                requirement={r}
                opportunityId={opportunityId}
                selected={selectedIds.has(r.id)}
                onToggleSelect={() => toggleSelect(r.id)}
              />
            ))}
          </div>
        )}

        {!isLoading && requirements && shouldVirtualize && (
          <div
            ref={parentRef}
            className="max-h-[700px] overflow-y-auto pr-1"
            style={{ contain: 'strict' }}
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((vi) => {
                const r = requirements[vi.index]!;
                return (
                  <div
                    key={r.id}
                    data-index={vi.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${vi.start}px)`,
                      paddingBottom: '8px',
                    }}
                  >
                    <RequirementCard
                      requirement={r}
                      opportunityId={opportunityId}
                      selected={selectedIds.has(r.id)}
                      onToggleSelect={() => toggleSelect(r.id)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
